import { wgsl } from './tslCompat';
import { qrot } from './quatUtils';
import { shapeEncodingHelpers } from './shapeEncoding';

// CcdResult.status is a bit set. A normal finite sweep miss carries FINITE and
// its algorithm bit; a certified bounding-sphere rejection carries FINITE and
// CHEAP_MISS. CONSERVATIVE_FALLBACK deliberately reports an early hit instead
// of a certified TOI; it is a visible false-positive policy, not a proof that
// one fixed contact feature guards every later rotating feature. FAIL_OPEN
// means that even such a finite fallback could not be formed: the caller must
// retain the ordinary discrete result, publish its sticky fatal state (plus
// optional detailed diagnostics), and must not manufacture a speculative row
// from invalid geometry.
export const CCD_STATUS_FINITE = 1 << 0;
export const CCD_STATUS_INITIAL_OVERLAP = 1 << 1;
export const CCD_STATUS_ANALYTIC = 1 << 2;
export const CCD_STATUS_CONSERVATIVE_ADVANCEMENT = 1 << 3;
export const CCD_STATUS_ITERATION_LIMIT = 1 << 4;
export const CCD_STATUS_FAIL_OPEN = 1 << 5;
export const CCD_STATUS_UNSUPPORTED = 1 << 6;
export const CCD_STATUS_NO_CLOSING = 1 << 7;
export const CCD_STATUS_DEGENERATE_AXIS = 1 << 8;
export const CCD_STATUS_NUMERIC_TOLERANCE = 1 << 9;
export const CCD_STATUS_INVALID_INPUT = 1 << 10;
export const CCD_STATUS_NON_FINITE = 1 << 11;
export const CCD_STATUS_CHEAP_MISS = 1 << 12;
// A finite, deliberately early contact produced when the exact sweep cannot
// finish its proof.  This is a conservative false-positive, not a certified
// TOI, and is intentionally distinct from FAIL_OPEN.
export const CCD_STATUS_CONSERVATIVE_FALLBACK = 1 << 13;

export const CCD_FEATURE_INVALID = 0xffffffff;
export const CCD_FEATURE_SPHERE_SPHERE = 6;
export const CCD_CA_MAX_ITERATIONS = 24;

/**
 * Pure WGSL helpers for opt-in swept narrow phase.
 *
 * Public WGSL entry points all use the same pose/shape argument order:
 *
 *   ccdSweepSphereSphere(...)
 *   ccdSweepSphereObb(...), ccdSweepObbSphere(...)
 *   ccdSweepObbObb(...)
 *   ccdSweepShapePair(...)
 *
 * A pose is linearly interpolated in translation and shortest-path nlerped in
 * rotation over normalized time [0, 1]. Shape encoding is identical to the
 * regular contact path: shape.x is packed metadata, shape.yzw is a box half
 * extent, and shape.y is a sphere radius.
 *
 * The module has no bindings and no side effects. Supplying it as a dependency
 * of a wgslFn therefore cannot change the ordinary discrete collision path.
 */
export const ccdHelpers = wgsl(/* wgsl */`
      const CCD_STATUS_FINITE: u32 = ${CCD_STATUS_FINITE}u;
      const CCD_STATUS_INITIAL_OVERLAP: u32 = ${CCD_STATUS_INITIAL_OVERLAP}u;
      const CCD_STATUS_ANALYTIC: u32 = ${CCD_STATUS_ANALYTIC}u;
      const CCD_STATUS_CONSERVATIVE_ADVANCEMENT: u32 = ${CCD_STATUS_CONSERVATIVE_ADVANCEMENT}u;
      const CCD_STATUS_ITERATION_LIMIT: u32 = ${CCD_STATUS_ITERATION_LIMIT}u;
      const CCD_STATUS_FAIL_OPEN: u32 = ${CCD_STATUS_FAIL_OPEN}u;
      const CCD_STATUS_UNSUPPORTED: u32 = ${CCD_STATUS_UNSUPPORTED}u;
      const CCD_STATUS_NO_CLOSING: u32 = ${CCD_STATUS_NO_CLOSING}u;
      const CCD_STATUS_DEGENERATE_AXIS: u32 = ${CCD_STATUS_DEGENERATE_AXIS}u;
      const CCD_STATUS_NUMERIC_TOLERANCE: u32 = ${CCD_STATUS_NUMERIC_TOLERANCE}u;
      const CCD_STATUS_INVALID_INPUT: u32 = ${CCD_STATUS_INVALID_INPUT}u;
      const CCD_STATUS_NON_FINITE: u32 = ${CCD_STATUS_NON_FINITE}u;
      const CCD_STATUS_CHEAP_MISS: u32 = ${CCD_STATUS_CHEAP_MISS}u;
      const CCD_STATUS_CONSERVATIVE_FALLBACK: u32 = ${CCD_STATUS_CONSERVATIVE_FALLBACK}u;

      const CCD_FEATURE_INVALID: u32 = ${CCD_FEATURE_INVALID}u;
      const CCD_FEATURE_SPHERE_SPHERE: u32 = ${CCD_FEATURE_SPHERE_SPHERE}u;
      const CCD_CA_MAX_ITERATIONS: u32 = ${CCD_CA_MAX_ITERATIONS}u;

      // 16 f32 ulps. The world-coordinate term is intentional: at large world
      // offsets a shape-only tolerance is smaller than representable spacing.
      const CCD_RELATIVE_EPSILON: f32 = 1.9073486328125e-6;
      const CCD_TIME_EPSILON: f32 = 1.0e-6;
      const CCD_AXIS_EPSILON_SQUARED: f32 = 1.0e-8;
      // Keeping accepted inputs below this range prevents dot products from
      // overflowing even before a result can be checked for finiteness.
      const CCD_SAFE_COMPONENT_LIMIT: f32 = 1.0e18;
      const CCD_SAFE_QUAT_COMPONENT_LIMIT: f32 = 1.0e9;

      struct CcdResult {
        hit: u32,
        status: u32,
        feature: u32,
        iterations: u32,
        toi: f32,
        separation: f32,
        resultPadding: vec2f,
        normal: vec3f,
        normalPadding: f32,
        witnessA: vec3f,
        witnessAPadding: f32,
        witnessB: vec3f,
        witnessBPadding: f32,
      };

      struct CcdSeparationSample {
        finite: u32,
        feature: u32,
        status: u32,
        samplePadding: u32,
        separation: f32,
        separationPadding: vec3f,
        normal: vec3f,
        normalPadding: f32,
        witnessA: vec3f,
        witnessAPadding: f32,
        witnessB: vec3f,
        witnessBPadding: f32,
      };

      struct CcdObbFrame {
        center: vec3f,
        centerPadding: f32,
        half: vec3f,
        halfPadding: f32,
        axis0: vec3f,
        axis0Padding: f32,
        axis1: vec3f,
        axis1Padding: f32,
        axis2: vec3f,
        axis2Padding: f32,
      };

      struct CcdAxisSample {
        valid: u32,
        feature: u32,
        axisPadding: vec2u,
        separation: f32,
        separationPadding: vec3f,
        normal: vec3f,
        normalPadding: f32,
      };

      fn ccdScalarFinite(value: f32) -> bool {
        return value == value && abs(value) <= CCD_SAFE_COMPONENT_LIMIT;
      }

      fn ccdVec3Finite(value: vec3f) -> bool {
        return all(value == value)
          && all(abs(value) <= vec3f(CCD_SAFE_COMPONENT_LIMIT));
      }

      fn ccdQuatFinite(value: vec4f) -> bool {
        return all(value == value)
          && all(abs(value) <= vec4f(CCD_SAFE_QUAT_COMPONENT_LIMIT));
      }

      fn ccdQuatValid(value: vec4f) -> bool {
        if (!ccdQuatFinite(value)) {
          return false;
        }
        let lengthSquared = dot(value, value);
        return ccdScalarFinite(lengthSquared) && lengthSquared > 1.0e-20;
      }

      fn ccdExtentValid(value: vec3f) -> bool {
        return ccdVec3Finite(value) && all(value >= vec3f(0.0));
      }

      fn ccdMaxAbs3(value: vec3f) -> f32 {
        return max(max(abs(value.x), abs(value.y)), abs(value.z));
      }

      fn ccdPairScale(
        currentPositionA: vec3f,
        targetPositionA: vec3f,
        currentPositionB: vec3f,
        targetPositionB: vec3f,
        extentA: vec3f,
        extentB: vec3f,
      ) -> f32 {
        var scale = 1.0;
        scale = max(scale, ccdMaxAbs3(currentPositionA));
        scale = max(scale, ccdMaxAbs3(targetPositionA));
        scale = max(scale, ccdMaxAbs3(currentPositionB));
        scale = max(scale, ccdMaxAbs3(targetPositionB));
        scale = max(scale, ccdMaxAbs3(extentA));
        scale = max(scale, ccdMaxAbs3(extentB));
        return scale;
      }

      fn ccdTolerance(scale: f32, requestedMargin: f32) -> f32 {
        return max(requestedMargin, scale * CCD_RELATIVE_EPSILON);
      }

      // Exact center-line distance in real arithmetic, rounded downward by a
      // deliberately generous f32 error allowance. Every supported shape is
      // contained by its orientation-independent bounding sphere, so a value
      // larger than the two radii plus tolerance is a strict swept miss even
      // when either box rotates. A negative return means the bound could not
      // be certified and the caller must continue into the exact sweep.
      fn ccdRelativeSweptBoundingSphereLowerBound(
        currentPositionA: vec3f,
        targetPositionA: vec3f,
        currentPositionB: vec3f,
        targetPositionB: vec3f,
      ) -> f32 {
        let relativeStart = currentPositionB - currentPositionA;
        let relativeMotion = (targetPositionB - currentPositionB)
          - (targetPositionA - currentPositionA);
        let motionLengthSquared = dot(relativeMotion, relativeMotion);
        let startMotionDot = dot(relativeStart, relativeMotion);
        if (!ccdScalarFinite(motionLengthSquared)
          || !ccdScalarFinite(startMotionDot)) {
          return -1.0;
        }

        var closestTime = 0.0;
        if (motionLengthSquared > 1.0e-30) {
          closestTime = clamp(-startMotionDot / motionLengthSquared, 0.0, 1.0);
        }
        let closestDelta = relativeStart + relativeMotion * closestTime;
        let centerDistance = length(closestDelta);
        if (!ccdScalarFinite(centerDistance)) {
          return -1.0;
        }

        let relativeScale = max(
          1.0,
          max(ccdMaxAbs3(relativeStart), ccdMaxAbs3(relativeMotion)),
        );
        // CCD_RELATIVE_EPSILON already represents 16 f32 ulps. Doubling it
        // covers the subtraction, projection, fused multiply-add and length
        // rounding without risking a false-negative rejection.
        let arithmeticAllowance = 2.0 * relativeScale * CCD_RELATIVE_EPSILON;
        return max(centerDistance - arithmeticAllowance, 0.0);
      }

      fn ccdCheapSweptBoundingSphereMissResult(
        lowerBound: f32,
        combinedRadius: f32,
        currentPositionA: vec3f,
        currentPositionB: vec3f,
      ) -> CcdResult {
        return ccdMakeResult(
          0u,
          CCD_STATUS_FINITE | CCD_STATUS_CHEAP_MISS,
          CCD_FEATURE_INVALID,
          0u,
          1.0,
          lowerBound - combinedRadius,
          vec3f(0.0, 1.0, 0.0),
          currentPositionA,
          currentPositionB,
        );
      }

      fn ccdNormalizeQuat(value: vec4f) -> vec4f {
        return value * inverseSqrt(dot(value, value));
      }

      fn ccdInterpolateQuat(currentValue: vec4f, targetValue: vec4f, t: f32) -> vec4f {
        let currentUnit = ccdNormalizeQuat(currentValue);
        var targetUnit = ccdNormalizeQuat(targetValue);
        if (dot(currentUnit, targetUnit) < 0.0) {
          targetUnit = -targetUnit;
        }
        return normalize(mix(currentUnit, targetUnit, t));
      }

      // Maximum angular speed of shortest-path quaternion nlerp with respect
      // to normalized sweep time. For quaternion arc alpha <= pi/2, the exact
      // peak is 4*sin(alpha)/(1+cos(alpha)). This is tighter and safer than
      // treating the total endpoint angle as a constant-speed bound.
      fn ccdQuatAngularSpeedBound(currentValue: vec4f, targetValue: vec4f) -> f32 {
        let currentUnit = ccdNormalizeQuat(currentValue);
        let targetUnit = ccdNormalizeQuat(targetValue);
        let cosine = clamp(abs(dot(currentUnit, targetUnit)), 0.0, 1.0);
        let sine = sqrt(max(1.0 - cosine * cosine, 0.0));
        return 4.0 * sine / max(1.0 + cosine, 1.0e-6);
      }

      fn ccdShapeSweepRadius(shape: vec4f) -> f32 {
        let extent = max(shape.yzw, vec3f(0.0));
        return select(
          length(extent),
          extent.x,
          decodeShapeType(shape.x) == SHAPE_TYPE_SPHERE,
        );
      }

      // Reject the expensive sweep only after proving that the shapes cannot
      // meet during this entire substep.  For supported finite geometry the
      // exact start sample supplies a separation lower bound.  The relative
      // center translation plus radius * peak quaternion-nlerp angular speed
      // bounds the full relative path length of every point.  Distance between
      // two compact sets is 1-Lipschitz under those point motions, hence
      //
      //   start separation > full motion upper bound + f32 tolerance
      //
      // is a strict miss.  The orientation-independent swept bounding-sphere
      // lower bound is an independent strict proof.  Every unsupported,
      // invalid, non-finite, or numerically ambiguous case returns true and
      // therefore reaches ccdSweepShapePair's visible fail-open/fallback path.
      fn ccdShouldRunSpeculativeSweep(
        currentPositionA: vec3f,
        currentQuaternionA: vec4f,
        targetPositionA: vec3f,
        targetQuaternionA: vec4f,
        shapeA: vec4f,
        currentPositionB: vec3f,
        currentQuaternionB: vec4f,
        targetPositionB: vec3f,
        targetQuaternionB: vec4f,
        shapeB: vec4f,
      ) -> bool {
        if (!ccdVec3Finite(currentPositionA)
          || !ccdVec3Finite(targetPositionA)
          || !ccdVec3Finite(currentPositionB)
          || !ccdVec3Finite(targetPositionB)) {
          return true;
        }

        let typeA = decodeShapeType(shapeA.x);
        let typeB = decodeShapeType(shapeB.x);
        let supportedA = typeA == SHAPE_TYPE_BOX || typeA == SHAPE_TYPE_SPHERE;
        let supportedB = typeB == SHAPE_TYPE_BOX || typeB == SHAPE_TYPE_SPHERE;
        if (!supportedA || !supportedB) {
          return true;
        }

        let sphereA = typeA == SHAPE_TYPE_SPHERE;
        let sphereB = typeB == SHAPE_TYPE_SPHERE;
        let geometryAValid = select(
          ccdExtentValid(shapeA.yzw)
            && ccdQuatValid(currentQuaternionA)
            && ccdQuatValid(targetQuaternionA),
          ccdScalarFinite(shapeA.y) && shapeA.y >= 0.0,
          sphereA,
        );
        let geometryBValid = select(
          ccdExtentValid(shapeB.yzw)
            && ccdQuatValid(currentQuaternionB)
            && ccdQuatValid(targetQuaternionB),
          ccdScalarFinite(shapeB.y) && shapeB.y >= 0.0,
          sphereB,
        );
        if (!geometryAValid || !geometryBValid) {
          return true;
        }

        let extentA = select(shapeA.yzw, vec3f(shapeA.y), sphereA);
        let extentB = select(shapeB.yzw, vec3f(shapeB.y), sphereB);
        let pairScale = ccdPairScale(
          currentPositionA,
          targetPositionA,
          currentPositionB,
          targetPositionB,
          extentA,
          extentB,
        );
        // Two base allowances cover opposite-direction rounding of both the
        // sampled separation lower bound and the full-motion upper bound.
        let arithmeticTolerance = 2.0 * ccdTolerance(pairScale, 0.0);

        // This proof is independent of rotation because each supported shape
        // stays inside its endpoint-invariant bounding sphere.
        let sweptCenterLowerBound = ccdRelativeSweptBoundingSphereLowerBound(
          currentPositionA,
          targetPositionA,
          currentPositionB,
          targetPositionB,
        );
        let radiusA = ccdShapeSweepRadius(shapeA);
        let radiusB = ccdShapeSweepRadius(shapeB);
        let combinedRadius = radiusA + radiusB;
        if (sweptCenterLowerBound >= 0.0
          && ccdScalarFinite(radiusA)
          && ccdScalarFinite(radiusB)
          && ccdScalarFinite(combinedRadius)
          && sweptCenterLowerBound > combinedRadius + arithmeticTolerance) {
          return false;
        }

        let startSample = ccdStartShapePairSeparation(
          currentPositionA,
          currentQuaternionA,
          shapeA,
          currentPositionB,
          currentQuaternionB,
          shapeB,
        );
        if (startSample.finite == 0u) {
          return true;
        }

        let relativeTranslation = (targetPositionA - currentPositionA)
          - (targetPositionB - currentPositionB);
        var angularEnvelopeA = 0.0;
        var angularEnvelopeB = 0.0;
        if (!sphereA) {
          angularEnvelopeA = ccdShapeSweepRadius(shapeA)
            * ccdQuatAngularSpeedBound(currentQuaternionA, targetQuaternionA);
        }
        if (!sphereB) {
          angularEnvelopeB = ccdShapeSweepRadius(shapeB)
            * ccdQuatAngularSpeedBound(currentQuaternionB, targetQuaternionB);
        }
        let fullMotionUpperBound = length(relativeTranslation)
          + angularEnvelopeA
          + angularEnvelopeB;
        if (!ccdScalarFinite(fullMotionUpperBound)) {
          return true;
        }

        return !(startSample.separation
          > fullMotionUpperBound + arithmeticTolerance);
      }

      fn ccdMakeResult(
        hit: u32,
        status: u32,
        feature: u32,
        iterations: u32,
        toi: f32,
        separation: f32,
        normal: vec3f,
        witnessA: vec3f,
        witnessB: vec3f,
      ) -> CcdResult {
        return CcdResult(
          hit,
          status,
          feature,
          iterations,
          toi,
          separation,
          vec2f(0.0),
          normal,
          0.0,
          witnessA,
          0.0,
          witnessB,
          0.0,
        );
      }

      fn ccdFailOpen(status: u32) -> CcdResult {
        return ccdMakeResult(
          0u,
          status | CCD_STATUS_FAIL_OPEN,
          CCD_FEATURE_INVALID,
          0u,
          1.0,
          0.0,
          vec3f(0.0, 1.0, 0.0),
          vec3f(0.0),
          vec3f(0.0),
        );
      }

      fn ccdMakeSeparationSample(
        feature: u32,
        status: u32,
        separation: f32,
        normal: vec3f,
        witnessA: vec3f,
        witnessB: vec3f,
      ) -> CcdSeparationSample {
        let finite = ccdScalarFinite(separation)
          && ccdVec3Finite(normal)
          && ccdVec3Finite(witnessA)
          && ccdVec3Finite(witnessB);
        return CcdSeparationSample(
          select(0u, 1u, finite),
          feature,
          status,
          0u,
          separation,
          vec3f(0.0),
          normal,
          0.0,
          witnessA,
          0.0,
          witnessB,
          0.0,
        );
      }

      fn ccdResultFromSample(
        hit: u32,
        status: u32,
        iterations: u32,
        toi: f32,
        sample: CcdSeparationSample,
      ) -> CcdResult {
        if (sample.finite == 0u || !ccdScalarFinite(toi)) {
          return ccdFailOpen(status | CCD_STATUS_NON_FINITE);
        }
        return ccdMakeResult(
          hit,
          status | sample.status,
          sample.feature,
          iterations,
          clamp(toi, 0.0, 1.0),
          sample.separation,
          sample.normal,
          sample.witnessA,
          sample.witnessB,
        );
      }

      // Turn a numerical/iteration uncertainty into a deliberately early
      // finite contact whenever a valid sampled separating plane still
      // exists.  Such a row may be a false positive, but cannot silently turn
      // an unresolved sweep into a miss.  Invalid samples remain FAIL_OPEN so
      // the caller can publish fatal diagnostics rather than claim safety.
      fn ccdConservativeFallbackFromSample(
        status: u32,
        iterations: u32,
        toi: f32,
        sample: CcdSeparationSample,
      ) -> CcdResult {
        if (sample.finite == 0u || !ccdScalarFinite(toi)) {
          return ccdFailOpen(status | CCD_STATUS_NON_FINITE);
        }
        return ccdResultFromSample(
          1u,
          status
            | CCD_STATUS_FINITE
            | CCD_STATUS_NUMERIC_TOLERANCE
            | CCD_STATUS_CONSERVATIVE_FALLBACK,
          iterations,
          toi,
          sample,
        );
      }

      fn ccdSphereSphereSample(
        positionA: vec3f,
        radiusA: f32,
        positionB: vec3f,
        radiusB: f32,
      ) -> CcdSeparationSample {
        let delta = positionB - positionA;
        let distanceSquared = dot(delta, delta);
        var normal = vec3f(0.0, 1.0, 0.0);
        var distance = 0.0;
        if (distanceSquared > 1.0e-20) {
          distance = sqrt(distanceSquared);
          normal = delta / distance;
        }
        let witnessA = positionA + normal * radiusA;
        let witnessB = positionB - normal * radiusB;
        return ccdMakeSeparationSample(
          CCD_FEATURE_SPHERE_SPHERE,
          0u,
          distance - radiusA - radiusB,
          normal,
          witnessA,
          witnessB,
        );
      }

      fn ccdMakeObbFrame(center: vec3f, quaternion: vec4f, half: vec3f) -> CcdObbFrame {
        let unitQuaternion = ccdNormalizeQuat(quaternion);
        return CcdObbFrame(
          center,
          0.0,
          half,
          0.0,
          qrot(unitQuaternion, vec3f(1.0, 0.0, 0.0)),
          0.0,
          qrot(unitQuaternion, vec3f(0.0, 1.0, 0.0)),
          0.0,
          qrot(unitQuaternion, vec3f(0.0, 0.0, 1.0)),
          0.0,
        );
      }

      fn ccdObbAxis(frame: CcdObbFrame, axisIndex: u32) -> vec3f {
        if (axisIndex == 0u) {
          return frame.axis0;
        }
        if (axisIndex == 1u) {
          return frame.axis1;
        }
        return frame.axis2;
      }

      fn ccdAxisValue(value: vec3f, axisIndex: u32) -> f32 {
        if (axisIndex == 0u) {
          return value.x;
        }
        if (axisIndex == 1u) {
          return value.y;
        }
        return value.z;
      }

      fn ccdLeastAxis(value: vec3f) -> u32 {
        var result = 0u;
        var least = value.x;
        if (value.y < least) {
          least = value.y;
          result = 1u;
        }
        if (value.z < least) {
          result = 2u;
        }
        return result;
      }

      fn ccdDominantAbsAxis(value: vec3f) -> u32 {
        let absoluteValue = abs(value);
        var result = 0u;
        var greatest = absoluteValue.x;
        if (absoluteValue.y > greatest) {
          greatest = absoluteValue.y;
          result = 1u;
        }
        if (absoluteValue.z > greatest) {
          result = 2u;
        }
        return result;
      }

      fn ccdNonzeroSign(value: f32) -> f32 {
        return select(-1.0, 1.0, value >= 0.0);
      }

      fn ccdObbProjectedRadius(frame: CcdObbFrame, normal: vec3f) -> f32 {
        return frame.half.x * abs(dot(frame.axis0, normal))
          + frame.half.y * abs(dot(frame.axis1, normal))
          + frame.half.z * abs(dot(frame.axis2, normal));
      }

      fn ccdObbSupport(frame: CcdObbFrame, direction: vec3f) -> vec3f {
        let sign0 = select(-1.0, 1.0, dot(frame.axis0, direction) >= 0.0);
        let sign1 = select(-1.0, 1.0, dot(frame.axis1, direction) >= 0.0);
        let sign2 = select(-1.0, 1.0, dot(frame.axis2, direction) >= 0.0);
        return frame.center
          + frame.axis0 * (frame.half.x * sign0)
          + frame.axis1 * (frame.half.y * sign1)
          + frame.axis2 * (frame.half.z * sign2);
      }

      fn ccdObbAxisSample(
        frameA: CcdObbFrame,
        frameB: CcdObbFrame,
        rawAxis: vec3f,
        feature: u32,
      ) -> CcdAxisSample {
        let lengthSquared = dot(rawAxis, rawAxis);
        if (!ccdScalarFinite(lengthSquared) || lengthSquared <= CCD_AXIS_EPSILON_SQUARED) {
          return CcdAxisSample(
            0u,
            feature,
            vec2u(0u),
            -3.0e38,
            vec3f(0.0),
            vec3f(0.0, 1.0, 0.0),
            0.0,
          );
        }
        var normal = rawAxis * inverseSqrt(lengthSquared);
        let centerDelta = frameB.center - frameA.center;
        if (dot(centerDelta, normal) < 0.0) {
          normal = -normal;
        }
        let separation = dot(centerDelta, normal)
          - ccdObbProjectedRadius(frameA, normal)
          - ccdObbProjectedRadius(frameB, normal);
        return CcdAxisSample(
          1u,
          feature,
          vec2u(0u),
          separation,
          vec3f(0.0),
          normal,
          0.0,
        );
      }

      fn ccdSphereObbSample(
        spherePosition: vec3f,
        sphereRadius: f32,
        boxFrame: CcdObbFrame,
      ) -> CcdSeparationSample {
        let relative = spherePosition - boxFrame.center;
        let sphereLocal = vec3f(
          dot(relative, boxFrame.axis0),
          dot(relative, boxFrame.axis1),
          dot(relative, boxFrame.axis2)
        );
        let clampedLocal = clamp(sphereLocal, -boxFrame.half, boxFrame.half);
        let localDelta = sphereLocal - clampedLocal;
        let outsideDistanceSquared = dot(localDelta, localDelta);

        var featureAxis = 0u;
        var boxOutwardNormal = boxFrame.axis0;
        var boxPointLocal = clampedLocal;
        var separation = 0.0;

        if (outsideDistanceSquared > 1.0e-20) {
          let outsideDistance = sqrt(outsideDistanceSquared);
          let localOutwardNormal = localDelta / outsideDistance;
          boxOutwardNormal = boxFrame.axis0 * localOutwardNormal.x
            + boxFrame.axis1 * localOutwardNormal.y
            + boxFrame.axis2 * localOutwardNormal.z;
          featureAxis = ccdDominantAbsAxis(localDelta);
          separation = outsideDistance - sphereRadius;
        } else {
          let faceGap = boxFrame.half - abs(sphereLocal);
          featureAxis = ccdLeastAxis(faceGap);
          let faceSign = ccdNonzeroSign(ccdAxisValue(sphereLocal, featureAxis));
          if (featureAxis == 0u) {
            boxPointLocal = vec3f(faceSign * boxFrame.half.x, sphereLocal.y, sphereLocal.z);
          } else if (featureAxis == 1u) {
            boxPointLocal = vec3f(sphereLocal.x, faceSign * boxFrame.half.y, sphereLocal.z);
          } else {
            boxPointLocal = vec3f(sphereLocal.x, sphereLocal.y, faceSign * boxFrame.half.z);
          }
          boxOutwardNormal = ccdObbAxis(boxFrame, featureAxis) * faceSign;
          separation = -sphereRadius - ccdAxisValue(faceGap, featureAxis);
        }

        let normal = -boxOutwardNormal;
        let witnessA = spherePosition + normal * sphereRadius;
        let witnessB = boxFrame.center
          + boxFrame.axis0 * boxPointLocal.x
          + boxFrame.axis1 * boxPointLocal.y
          + boxFrame.axis2 * boxPointLocal.z;
        // The box is body B, matching the regular contact path's features 3..5.
        return ccdMakeSeparationSample(
          3u + featureAxis,
          0u,
          separation,
          normal,
          witnessA,
          witnessB,
        );
      }

      fn ccdObbObbSample(frameA: CcdObbFrame, frameB: CcdObbFrame) -> CcdSeparationSample {
        var best = ccdObbAxisSample(frameA, frameB, frameA.axis0, 0u);
        var degenerateAxis = false;

        for (var axisAIndex = 1u; axisAIndex < 3u; axisAIndex++) {
          let candidate = ccdObbAxisSample(
            frameA,
            frameB,
            ccdObbAxis(frameA, axisAIndex),
            axisAIndex,
          );
          if (candidate.valid > 0u && candidate.separation > best.separation) {
            best = candidate;
          }
        }
        for (var axisBIndex = 0u; axisBIndex < 3u; axisBIndex++) {
          let candidate = ccdObbAxisSample(
            frameA,
            frameB,
            ccdObbAxis(frameB, axisBIndex),
            3u + axisBIndex,
          );
          if (candidate.valid > 0u && candidate.separation > best.separation) {
            best = candidate;
          }
        }
        for (var axisAIndex = 0u; axisAIndex < 3u; axisAIndex++) {
          for (var axisBIndex = 0u; axisBIndex < 3u; axisBIndex++) {
            let candidate = ccdObbAxisSample(
              frameA,
              frameB,
              cross(ccdObbAxis(frameA, axisAIndex), ccdObbAxis(frameB, axisBIndex)),
              6u + axisAIndex * 3u + axisBIndex,
            );
            if (candidate.valid == 0u) {
              degenerateAxis = true;
            } else if (candidate.separation > best.separation) {
              best = candidate;
            }
          }
        }

        let witnessA = ccdObbSupport(frameA, best.normal);
        let witnessB = ccdObbSupport(frameB, -best.normal);
        return ccdMakeSeparationSample(
          best.feature,
          select(0u, CCD_STATUS_DEGENERATE_AXIS, degenerateAxis),
          best.separation,
          best.normal,
          witnessA,
          witnessB,
        );
      }

      // Exact for sphere/sphere and sphere/box. For box/box the maximum SAT
      // axis gap is a lower bound on Euclidean set distance, which is exactly
      // what the strict activation proof requires.
      fn ccdStartShapePairSeparation(
        positionA: vec3f,
        quaternionA: vec4f,
        shapeA: vec4f,
        positionB: vec3f,
        quaternionB: vec4f,
        shapeB: vec4f,
      ) -> CcdSeparationSample {
        let sphereA = decodeShapeType(shapeA.x) == SHAPE_TYPE_SPHERE;
        let sphereB = decodeShapeType(shapeB.x) == SHAPE_TYPE_SPHERE;
        if (sphereA && sphereB) {
          return ccdSphereSphereSample(positionA, shapeA.y, positionB, shapeB.y);
        }
        if (sphereA) {
          return ccdSphereObbSample(
            positionA,
            shapeA.y,
            ccdMakeObbFrame(positionB, quaternionB, shapeB.yzw),
          );
        }
        if (sphereB) {
          // Only separation is consumed by the activation proof; reversing
          // witnesses/normal cannot change that scalar lower bound.
          return ccdSphereObbSample(
            positionB,
            shapeB.y,
            ccdMakeObbFrame(positionA, quaternionA, shapeA.yzw),
          );
        }
        return ccdObbObbSample(
          ccdMakeObbFrame(positionA, quaternionA, shapeA.yzw),
          ccdMakeObbFrame(positionB, quaternionB, shapeB.yzw),
        );
      }

      fn ccdSweepSphereSphere(
        currentPositionA: vec3f,
        currentQuaternionA: vec4f,
        targetPositionA: vec3f,
        targetQuaternionA: vec4f,
        shapeA: vec4f,
        currentPositionB: vec3f,
        currentQuaternionB: vec4f,
        targetPositionB: vec3f,
        targetQuaternionB: vec4f,
        shapeB: vec4f,
        requestedMargin: f32,
      ) -> CcdResult {
        // Sphere orientation is deliberately ignored, including its validity.
        // It has no geometric effect and rejecting it would introduce a false
        // negative that the regular sphere path does not have.
        let radiusA = shapeA.y;
        let radiusB = shapeB.y;
        if (!ccdVec3Finite(currentPositionA)
          || !ccdVec3Finite(targetPositionA)
          || !ccdVec3Finite(currentPositionB)
          || !ccdVec3Finite(targetPositionB)
          || !ccdScalarFinite(radiusA)
          || !ccdScalarFinite(radiusB)
          || !ccdScalarFinite(requestedMargin)) {
          return ccdFailOpen(CCD_STATUS_INVALID_INPUT | CCD_STATUS_NON_FINITE);
        }
        if (radiusA < 0.0 || radiusB < 0.0 || requestedMargin < 0.0) {
          return ccdFailOpen(CCD_STATUS_INVALID_INPUT);
        }

        let extentA = vec3f(radiusA);
        let extentB = vec3f(radiusB);
        let scale = ccdPairScale(
          currentPositionA,
          targetPositionA,
          currentPositionB,
          targetPositionB,
          extentA,
          extentB,
        );
        let tolerance = ccdTolerance(scale, requestedMargin);
        let relativePosition = currentPositionB - currentPositionA;
        let relativeVelocity = (targetPositionB - currentPositionB)
          - (targetPositionA - currentPositionA);
        let expandedRadius = radiusA + radiusB + tolerance;
        let constantTerm = dot(relativePosition, relativePosition) - expandedRadius * expandedRadius;
        let startSample = ccdSphereSphereSample(
          currentPositionA,
          radiusA,
          currentPositionB,
          radiusB,
        );
        let baseStatus = CCD_STATUS_FINITE | CCD_STATUS_ANALYTIC;
        if (startSample.finite == 0u) {
          return ccdFailOpen(CCD_STATUS_ANALYTIC | CCD_STATUS_NON_FINITE);
        }
        if (!ccdScalarFinite(constantTerm)) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            0.0,
            startSample,
          );
        }
        if (constantTerm <= 0.0) {
          let toleranceStatus = select(
            CCD_STATUS_NUMERIC_TOLERANCE,
            CCD_STATUS_INITIAL_OVERLAP,
            startSample.separation <= 0.0,
          );
          return ccdResultFromSample(1u, baseStatus | toleranceStatus, 1u, 0.0, startSample);
        }

        let quadraticTerm = dot(relativeVelocity, relativeVelocity);
        let linearTerm = dot(relativePosition, relativeVelocity);
        let motionEpsilon = tolerance * CCD_TIME_EPSILON;
        if (!ccdScalarFinite(quadraticTerm) || !ccdScalarFinite(linearTerm)) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            0.0,
            startSample,
          );
        }
        if (linearTerm >= 0.0) {
          return ccdResultFromSample(
            0u,
            baseStatus | CCD_STATUS_NO_CLOSING,
            1u,
            1.0,
            startSample,
          );
        }
        if (quadraticTerm <= motionEpsilon * motionEpsilon) {
          return ccdConservativeFallbackFromSample(
            baseStatus,
            1u,
            0.0,
            startSample,
          );
        }

        var discriminant = linearTerm * linearTerm - quadraticTerm * constantTerm;
        let discriminantScale = max(
          max(linearTerm * linearTerm, abs(quadraticTerm * constantTerm)),
          1.0,
        );
        let discriminantTolerance = discriminantScale * CCD_RELATIVE_EPSILON;
        if (!ccdScalarFinite(discriminant)) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            0.0,
            startSample,
          );
        }
        if (discriminant < -discriminantTolerance) {
          return ccdResultFromSample(0u, baseStatus, 1u, 1.0, startSample);
        }
        discriminant = max(discriminant, 0.0);
        let root = sqrt(discriminant);
        // c / (-b + sqrt(d)) is the cancellation-resistant form of the
        // approaching root of a*t^2 + 2*b*t + c = 0.
        let denominator = -linearTerm + root;
        if (denominator <= 0.0 || !ccdScalarFinite(denominator)) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            0.0,
            startSample,
          );
        }
        let toi = constantTerm / denominator;
        if (!ccdScalarFinite(toi)) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            0.0,
            startSample,
          );
        }
        if (toi < -CCD_TIME_EPSILON) {
          return ccdConservativeFallbackFromSample(
            baseStatus,
            1u,
            0.0,
            startSample,
          );
        }
        if (toi > 1.0 + CCD_TIME_EPSILON) {
          return ccdResultFromSample(0u, baseStatus, 1u, 1.0, startSample);
        }

        let clampedToi = clamp(toi, 0.0, 1.0);
        let positionA = mix(currentPositionA, targetPositionA, clampedToi);
        let positionB = mix(currentPositionB, targetPositionB, clampedToi);
        let hitSample = ccdSphereSphereSample(positionA, radiusA, positionB, radiusB);
        if (hitSample.finite == 0u) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            0.0,
            startSample,
          );
        }
        return ccdResultFromSample(
          1u,
          baseStatus | CCD_STATUS_NUMERIC_TOLERANCE,
          1u,
          clampedToi,
          hitSample,
        );
      }

      fn ccdSweepSphereObb(
        currentPositionA: vec3f,
        currentQuaternionA: vec4f,
        targetPositionA: vec3f,
        targetQuaternionA: vec4f,
        shapeA: vec4f,
        currentPositionB: vec3f,
        currentQuaternionB: vec4f,
        targetPositionB: vec3f,
        targetQuaternionB: vec4f,
        shapeB: vec4f,
        requestedMargin: f32,
      ) -> CcdResult {
        let sphereRadius = shapeA.y;
        let boxHalf = shapeB.yzw;
        if (!ccdVec3Finite(currentPositionA)
          || !ccdVec3Finite(targetPositionA)
          || !ccdVec3Finite(currentPositionB)
          || !ccdVec3Finite(targetPositionB)
          || !ccdScalarFinite(sphereRadius)
          || !ccdExtentValid(boxHalf)
          || !ccdScalarFinite(requestedMargin)) {
          return ccdFailOpen(CCD_STATUS_INVALID_INPUT | CCD_STATUS_NON_FINITE);
        }
        if (sphereRadius < 0.0
          || requestedMargin < 0.0
          || !ccdQuatValid(currentQuaternionB)
          || !ccdQuatValid(targetQuaternionB)) {
          return ccdFailOpen(CCD_STATUS_INVALID_INPUT);
        }

        let sphereExtent = vec3f(sphereRadius);
        let scale = ccdPairScale(
          currentPositionA,
          targetPositionA,
          currentPositionB,
          targetPositionB,
          sphereExtent,
          boxHalf,
        );
        let tolerance = ccdTolerance(scale, requestedMargin);
        let relativeTranslation = (targetPositionB - currentPositionB)
          - (targetPositionA - currentPositionA);
        let sphereBoundLower = ccdRelativeSweptBoundingSphereLowerBound(
          currentPositionA,
          targetPositionA,
          currentPositionB,
          targetPositionB,
        );
        let combinedSphereBound = sphereRadius + length(boxHalf);
        if (sphereBoundLower >= 0.0
          && sphereBoundLower > combinedSphereBound + tolerance) {
          return ccdCheapSweptBoundingSphereMissResult(
            sphereBoundLower,
            combinedSphereBound,
            currentPositionA,
            currentPositionB,
          );
        }
        let boxAngularSpeed = ccdQuatAngularSpeedBound(currentQuaternionB, targetQuaternionB);
        let rawSpeedBound = length(relativeTranslation) + boxAngularSpeed * length(boxHalf);
        // Inflate the real-arithmetic bound by the same absolute f32 error
        // allowance used by the separation samples. This prevents a rounded-
        // down speed from turning the strict remaining-motion test into a
        // false miss.
        let speedBound = rawSpeedBound + tolerance;
        let baseStatus = CCD_STATUS_FINITE | CCD_STATUS_CONSERVATIVE_ADVANCEMENT;
        let motionEpsilon = tolerance * CCD_TIME_EPSILON;
        var t = 0.0;
        var sampledT = 0.0;
        var lastSample = ccdSphereObbSample(
          currentPositionA,
          sphereRadius,
          ccdMakeObbFrame(currentPositionB, currentQuaternionB, boxHalf),
        );
        if (!ccdScalarFinite(speedBound)) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            sampledT,
            lastSample,
          );
        }

        for (var iteration = 0u; iteration < CCD_CA_MAX_ITERATIONS; iteration++) {
          // lastSample already contains the t=0 query. Reuse it on the first
          // iteration instead of repeating all closest-point/OBB work.
          if (iteration > 0u) {
            let spherePosition = mix(currentPositionA, targetPositionA, t);
            let boxPosition = mix(currentPositionB, targetPositionB, t);
            let boxQuaternion = ccdInterpolateQuat(currentQuaternionB, targetQuaternionB, t);
            let nextSample = ccdSphereObbSample(
              spherePosition,
              sphereRadius,
              ccdMakeObbFrame(boxPosition, boxQuaternion, boxHalf),
            );
            if (nextSample.finite == 0u) {
              return ccdConservativeFallbackFromSample(
                baseStatus | CCD_STATUS_NON_FINITE,
                iteration + 1u,
                sampledT,
                lastSample,
              );
            }
            lastSample = nextSample;
            sampledT = t;
          }
          if (lastSample.finite == 0u) {
            return ccdFailOpen(CCD_STATUS_CONSERVATIVE_ADVANCEMENT | CCD_STATUS_NON_FINITE);
          }
          if (lastSample.separation <= tolerance) {
            var hitStatus = baseStatus | CCD_STATUS_NUMERIC_TOLERANCE;
            if (t == 0.0 && lastSample.separation <= 0.0) {
              hitStatus |= CCD_STATUS_INITIAL_OVERLAP;
            }
            return ccdResultFromSample(1u, hitStatus, iteration + 1u, t, lastSample);
          }
          if (t >= 1.0) {
            return ccdResultFromSample(0u, baseStatus, iteration + 1u, 1.0, lastSample);
          }

          let gap = lastSample.separation - tolerance;
          let remaining = 1.0 - t;
          if (gap > speedBound * remaining + tolerance * CCD_RELATIVE_EPSILON) {
            return ccdResultFromSample(
              0u,
              baseStatus | CCD_STATUS_NO_CLOSING,
              iteration + 1u,
              1.0,
              lastSample,
            );
          }
          // A tiny speed is not itself a miss proof: a correspondingly tiny
          // positive gap can still close. If the strict remaining-motion test
          // above was inconclusive, emit an early conservative row.
          if (speedBound <= motionEpsilon) {
            return ccdConservativeFallbackFromSample(
              baseStatus,
              iteration + 1u,
              t,
              lastSample,
            );
          }
          let advance = 0.95 * gap / speedBound;
          if (!ccdScalarFinite(advance)) {
            return ccdConservativeFallbackFromSample(
              baseStatus | CCD_STATUS_NON_FINITE,
              iteration + 1u,
              t,
              lastSample,
            );
          }
          if (advance <= CCD_TIME_EPSILON) {
            return ccdResultFromSample(
              1u,
              baseStatus | CCD_STATUS_NUMERIC_TOLERANCE,
              iteration + 1u,
              t,
              lastSample,
            );
          }
          t = min(t + advance, 1.0);
        }

        return ccdConservativeFallbackFromSample(
          baseStatus | CCD_STATUS_ITERATION_LIMIT,
          CCD_CA_MAX_ITERATIONS,
          t,
          lastSample,
        );
      }

      fn ccdReverseResult(result: CcdResult) -> CcdResult {
        var feature = result.feature;
        if (feature >= 3u && feature <= 5u) {
          feature -= 3u;
        }
        return ccdMakeResult(
          result.hit,
          result.status,
          feature,
          result.iterations,
          result.toi,
          result.separation,
          -result.normal,
          result.witnessB,
          result.witnessA,
        );
      }

      fn ccdSweepObbSphere(
        currentPositionA: vec3f,
        currentQuaternionA: vec4f,
        targetPositionA: vec3f,
        targetQuaternionA: vec4f,
        shapeA: vec4f,
        currentPositionB: vec3f,
        currentQuaternionB: vec4f,
        targetPositionB: vec3f,
        targetQuaternionB: vec4f,
        shapeB: vec4f,
        requestedMargin: f32,
      ) -> CcdResult {
        return ccdReverseResult(ccdSweepSphereObb(
          currentPositionB,
          currentQuaternionB,
          targetPositionB,
          targetQuaternionB,
          shapeB,
          currentPositionA,
          currentQuaternionA,
          targetPositionA,
          targetQuaternionA,
          shapeA,
          requestedMargin,
        ));
      }

      fn ccdSweepObbObb(
        currentPositionA: vec3f,
        currentQuaternionA: vec4f,
        targetPositionA: vec3f,
        targetQuaternionA: vec4f,
        shapeA: vec4f,
        currentPositionB: vec3f,
        currentQuaternionB: vec4f,
        targetPositionB: vec3f,
        targetQuaternionB: vec4f,
        shapeB: vec4f,
        requestedMargin: f32,
      ) -> CcdResult {
        let halfA = shapeA.yzw;
        let halfB = shapeB.yzw;
        if (!ccdVec3Finite(currentPositionA)
          || !ccdVec3Finite(targetPositionA)
          || !ccdVec3Finite(currentPositionB)
          || !ccdVec3Finite(targetPositionB)
          || !ccdExtentValid(halfA)
          || !ccdExtentValid(halfB)
          || !ccdScalarFinite(requestedMargin)) {
          return ccdFailOpen(CCD_STATUS_INVALID_INPUT | CCD_STATUS_NON_FINITE);
        }
        if (requestedMargin < 0.0
          || !ccdQuatValid(currentQuaternionA)
          || !ccdQuatValid(targetQuaternionA)
          || !ccdQuatValid(currentQuaternionB)
          || !ccdQuatValid(targetQuaternionB)) {
          return ccdFailOpen(CCD_STATUS_INVALID_INPUT);
        }

        let scale = ccdPairScale(
          currentPositionA,
          targetPositionA,
          currentPositionB,
          targetPositionB,
          halfA,
          halfB,
        );
        let tolerance = ccdTolerance(scale, requestedMargin);
        let relativeTranslation = (targetPositionB - currentPositionB)
          - (targetPositionA - currentPositionA);
        let sphereBoundLower = ccdRelativeSweptBoundingSphereLowerBound(
          currentPositionA,
          targetPositionA,
          currentPositionB,
          targetPositionB,
        );
        let combinedSphereBound = length(halfA) + length(halfB);
        if (sphereBoundLower >= 0.0
          && sphereBoundLower > combinedSphereBound + tolerance) {
          return ccdCheapSweptBoundingSphereMissResult(
            sphereBoundLower,
            combinedSphereBound,
            currentPositionA,
            currentPositionB,
          );
        }
        let angularSpeedA = ccdQuatAngularSpeedBound(currentQuaternionA, targetQuaternionA);
        let angularSpeedB = ccdQuatAngularSpeedBound(currentQuaternionB, targetQuaternionB);
        let rawSpeedBound = length(relativeTranslation)
          + angularSpeedA * length(halfA)
          + angularSpeedB * length(halfB);
        let speedBound = rawSpeedBound + tolerance;
        let baseStatus = CCD_STATUS_FINITE | CCD_STATUS_CONSERVATIVE_ADVANCEMENT;
        let motionEpsilon = tolerance * CCD_TIME_EPSILON;
        var t = 0.0;
        var sampledT = 0.0;
        var lastSample = ccdObbObbSample(
          ccdMakeObbFrame(currentPositionA, currentQuaternionA, halfA),
          ccdMakeObbFrame(currentPositionB, currentQuaternionB, halfB),
        );
        if (!ccdScalarFinite(speedBound)) {
          return ccdConservativeFallbackFromSample(
            baseStatus | CCD_STATUS_NON_FINITE,
            1u,
            sampledT,
            lastSample,
          );
        }

        for (var iteration = 0u; iteration < CCD_CA_MAX_ITERATIONS; iteration++) {
          // The initialized sample is the first CA query; do not execute the
          // fifteen-axis SAT twice at t=0.
          if (iteration > 0u) {
            let positionA = mix(currentPositionA, targetPositionA, t);
            let positionB = mix(currentPositionB, targetPositionB, t);
            let quaternionA = ccdInterpolateQuat(currentQuaternionA, targetQuaternionA, t);
            let quaternionB = ccdInterpolateQuat(currentQuaternionB, targetQuaternionB, t);
            let nextSample = ccdObbObbSample(
              ccdMakeObbFrame(positionA, quaternionA, halfA),
              ccdMakeObbFrame(positionB, quaternionB, halfB),
            );
            if (nextSample.finite == 0u) {
              return ccdConservativeFallbackFromSample(
                baseStatus | CCD_STATUS_NON_FINITE,
                iteration + 1u,
                sampledT,
                lastSample,
              );
            }
            lastSample = nextSample;
            sampledT = t;
          }
          if (lastSample.finite == 0u) {
            return ccdFailOpen(CCD_STATUS_CONSERVATIVE_ADVANCEMENT | CCD_STATUS_NON_FINITE);
          }
          if (lastSample.separation <= tolerance) {
            var hitStatus = baseStatus | CCD_STATUS_NUMERIC_TOLERANCE;
            if (t == 0.0 && lastSample.separation <= 0.0) {
              hitStatus |= CCD_STATUS_INITIAL_OVERLAP;
            }
            return ccdResultFromSample(1u, hitStatus, iteration + 1u, t, lastSample);
          }
          if (t >= 1.0) {
            return ccdResultFromSample(0u, baseStatus, iteration + 1u, 1.0, lastSample);
          }

          let gap = lastSample.separation - tolerance;
          let remaining = 1.0 - t;
          if (gap > speedBound * remaining + tolerance * CCD_RELATIVE_EPSILON) {
            return ccdResultFromSample(
              0u,
              baseStatus | CCD_STATUS_NO_CLOSING,
              iteration + 1u,
              1.0,
              lastSample,
            );
          }
          if (speedBound <= motionEpsilon) {
            return ccdConservativeFallbackFromSample(
              baseStatus,
              iteration + 1u,
              t,
              lastSample,
            );
          }
          let advance = 0.95 * gap / speedBound;
          if (!ccdScalarFinite(advance)) {
            return ccdConservativeFallbackFromSample(
              baseStatus | CCD_STATUS_NON_FINITE,
              iteration + 1u,
              t,
              lastSample,
            );
          }
          if (advance <= CCD_TIME_EPSILON) {
            return ccdResultFromSample(
              1u,
              baseStatus | CCD_STATUS_NUMERIC_TOLERANCE,
              iteration + 1u,
              t,
              lastSample,
            );
          }
          t = min(t + advance, 1.0);
        }

        return ccdConservativeFallbackFromSample(
          baseStatus | CCD_STATUS_ITERATION_LIMIT,
          CCD_CA_MAX_ITERATIONS,
          t,
          lastSample,
        );
      }

      fn ccdSweepShapePair(
        currentPositionA: vec3f,
        currentQuaternionA: vec4f,
        targetPositionA: vec3f,
        targetQuaternionA: vec4f,
        shapeA: vec4f,
        currentPositionB: vec3f,
        currentQuaternionB: vec4f,
        targetPositionB: vec3f,
        targetQuaternionB: vec4f,
        shapeB: vec4f,
        requestedMargin: f32,
      ) -> CcdResult {
        let shapeTypeA = decodeShapeType(shapeA.x);
        let shapeTypeB = decodeShapeType(shapeB.x);
        if (shapeTypeA == SHAPE_TYPE_SPHERE && shapeTypeB == SHAPE_TYPE_SPHERE) {
          return ccdSweepSphereSphere(
            currentPositionA,
            currentQuaternionA,
            targetPositionA,
            targetQuaternionA,
            shapeA,
            currentPositionB,
            currentQuaternionB,
            targetPositionB,
            targetQuaternionB,
            shapeB,
            requestedMargin,
          );
        }
        if (shapeTypeA == SHAPE_TYPE_SPHERE && shapeTypeB == SHAPE_TYPE_BOX) {
          return ccdSweepSphereObb(
            currentPositionA,
            currentQuaternionA,
            targetPositionA,
            targetQuaternionA,
            shapeA,
            currentPositionB,
            currentQuaternionB,
            targetPositionB,
            targetQuaternionB,
            shapeB,
            requestedMargin,
          );
        }
        if (shapeTypeA == SHAPE_TYPE_BOX && shapeTypeB == SHAPE_TYPE_SPHERE) {
          return ccdSweepObbSphere(
            currentPositionA,
            currentQuaternionA,
            targetPositionA,
            targetQuaternionA,
            shapeA,
            currentPositionB,
            currentQuaternionB,
            targetPositionB,
            targetQuaternionB,
            shapeB,
            requestedMargin,
          );
        }
        if (shapeTypeA == SHAPE_TYPE_BOX && shapeTypeB == SHAPE_TYPE_BOX) {
          return ccdSweepObbObb(
            currentPositionA,
            currentQuaternionA,
            targetPositionA,
            targetQuaternionA,
            shapeA,
            currentPositionB,
            currentQuaternionB,
            targetPositionB,
            targetQuaternionB,
            shapeB,
            requestedMargin,
          );
        }
        return ccdFailOpen(CCD_STATUS_UNSUPPORTED);
      }
    `, [qrot, shapeEncodingHelpers]);
